import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApprovalQuestions, QuestionChip, type QuestionState, type ThreadEntry } from "../components/approvals/AskQuestion";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import { AlertTriangle, CheckCircle2, ChevronDown, CircleDashed, CircleSlash, Clock, ExternalLink, Play, Plus, XCircle } from "lucide-react";
import { Fragment, useState } from "react";
import { api, post } from "../lib/api";
import { useCan } from "../lib/session";
import type { FieldDef, Person } from "../lib/work";
import { Card, Empty, ErrorNote, Field, fmtDate, Modal, PageHeader } from "../components/ui";
import { PersonPicker, StatusPill, UploadButton } from "../components/work";
import { useOrg } from "./admin/organisation";
import { StepForm, type DecisionField } from "../components/workflow/StepForm";

const eur = (n: unknown) => (n === null || n === undefined || n === "" ? "" : `€${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`);

// ───────────────────────── Approvals ─────────────────────────
interface ApprovalRow { questionState?: QuestionState; id: string; ref: string; title: string; amount: string | null; status: string; createdAt: string; requestedByName: string | null; stepName: string | null; dueAt: string | null; policyKey: string; lineCount: number; quoteCount: number }

const POLICY_LABEL: Record<string, string> = { "procurement.purchase_request": "Purchase", "projects.variation": "Project variation", "people.leave": "Leave", "people.onboarding_access": "Onboarding", "people.probation_outcome": "Probation" };

interface DLine { lineNo?: number; description?: string; code?: string; specs?: string; note?: string; quantity?: number; unit?: string; unitPrice?: number; total?: number }
interface Details {
  lines?: DLine[];
  totals?: { subtotal?: number; vatRate?: number; vat?: number; total?: number };
  suppliers?: Array<{ name: string; email?: string; status?: "responded" | "sent" | "not_sent" | "declined" }>;
  quotes?: Array<{ supplier: string; total?: number; currency?: string; vatIncluded?: boolean; leadTime?: string; notes?: string; receivedAt?: string; awarded?: boolean; lines?: DLine[] }>;
  warnings?: string[];
  facts?: Array<{ label: string; value: string }>;
  links?: Array<{ label: string; url: string }>;
}

const money = (n: number | undefined) => (n === undefined || n === null ? "—" : `€${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const label = (k: string) => k.replace(/_/g, " ").replace(/([A-Z])/g, " $1").toLowerCase().replace(/^./, (c) => c.toUpperCase());
const lineTotal = (l: DLine) => l.total ?? (l.quantity !== undefined && l.unitPrice !== undefined ? l.quantity * l.unitPrice : undefined);
const dateish = (v?: string) => { if (!v) return "—"; const t = Date.parse(v); return Number.isNaN(t) ? v : new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); };
const SUPPLIER_STATUS: Record<string, [string, string]> = { responded: ["Responded", "bg-emerald-500/10 text-emerald-700"], sent: ["Waiting", "bg-amber-500/10 text-amber-700"], not_sent: ["Not yet sent", "bg-black/5 muted"], declined: ["Declined", "bg-red-500/10 text-red-700"] };

function Section({ title, count, children }: { title: string; count?: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-[var(--line)]">
      <h3 className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold">{title}{count && <span className="muted text-xs font-normal">{count}</span>}</h3>
      {children}
    </section>
  );
}

function QuoteRow({ q, lines }: { q: NonNullable<Details["quotes"]>[number]; lines: DLine[] }) {
  const [open, setOpen] = useState(!!q.awarded);
  const ql = q.lines ?? [];
  const byNo = new Map(lines.map((l, i) => [l.lineNo ?? i + 1, l]));
  return (
    <>
      <tr className="cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/5" onClick={() => setOpen((o) => !o)}>
        <td className="w-8"><ChevronDown size={14} className={clsx("transition-transform", open ? "rotate-0" : "-rotate-90")} /></td>
        <td><span className="font-medium">{q.supplier}</span>{ql.length > 0 && <span className="muted text-xs"> · {ql.length} line{ql.length > 1 ? "s" : ""}</span>}{q.notes && <div className="muted text-xs">{q.notes}</div>}</td>
        <td className="text-right font-semibold tabular-nums">{money(q.total)}</td>
        <td>{q.leadTime || "—"}</td>
        <td>{dateish(q.receivedAt)}</td>
        <td className="text-right">{q.awarded && <span className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white">Awarded</span>}</td>
      </tr>
      {open && ql.length > 0 && (
        <tr>
          <td colSpan={6} className="!p-0">
            <table className="table bg-black/[0.02] text-xs dark:bg-white/[0.03]">
              <tbody>
                {ql.map((l, i) => {
                  const ref = byNo.get(l.lineNo ?? i + 1);
                  return (
                    <tr key={i}>
                      <td className="w-8" />
                      <td>{l.lineNo ?? i + 1} · {l.description ?? ref?.description ?? ""}</td>
                      <td className="text-right tabular-nums">{l.quantity ?? ref?.quantity ?? ""}</td>
                      <td className="text-right tabular-nums">{money(l.unitPrice)}</td>
                      <td className="text-right tabular-nums">{money(lineTotal({ ...l, quantity: l.quantity ?? ref?.quantity }))}</td>
                    </tr>
                  );
                })}
                <tr><td /><td className="muted">Quote total{q.vatIncluded === false ? " (excl. VAT)" : q.vatIncluded ? " (incl. VAT)" : ""}</td><td /><td /><td className="text-right font-semibold tabular-nums">{money(q.total)}</td></tr>
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

/** What's being approved, laid out like the source app's RFQ: line items, suppliers asked, quotes (expandable to line prices), warnings, facts, links. */
export function ApprovalDetailsView({ context }: { context: Record<string, unknown> }) {
  const d = (context?.details ?? {}) as Details;
  const lines = d.lines ?? [];
  const rfq = !!(d.quotes?.length || d.suppliers?.length);
  const priced = !rfq && lines.some((l) => l.unitPrice !== undefined || l.total !== undefined);
  const plain = Object.entries(context ?? {}).filter(([k, v]) => k !== "details" && k !== "source" && !/Id$|^profile$|^requestTitle$/.test(k) && v !== null && v !== "" && typeof v !== "object");
  const facts = [...(d.facts ?? []), ...(lines.length || d.facts?.length || rfq ? [] : plain.map(([k, v]) => ({ label: label(k), value: String(v) })))];
  const nothing = !lines.length && !rfq && !facts.length && !d.links?.length && !d.warnings?.length;
  return (
    <div className="-mx-3 -mb-3">
      {lines.length > 0 && (
        <Section title="Line items">
          <div className="overflow-x-auto">
            <table className="table min-w-[32rem]">
              <thead>
                <tr>
                  <th className="w-10">#</th><th>Description</th><th className="!text-right">Qty</th><th>Unit</th>
                  {priced ? <><th className="!text-right">Unit price</th><th className="!text-right">Line total</th></> : <th>Specs / notes</th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td className="muted">{l.lineNo ?? i + 1}</td>
                    <td>{l.description}{priced && (l.code || l.specs || l.note) && <div className="muted text-xs">{[l.code && `Code ${l.code}`, l.specs, l.note].filter(Boolean).join(" · ")}</div>}</td>
                    <td className="text-right tabular-nums">{l.quantity ?? ""}</td>
                    <td>{l.unit ?? ""}</td>
                    {priced ? <><td className="text-right tabular-nums">{money(l.unitPrice)}</td><td className="text-right tabular-nums">{money(lineTotal(l))}</td></> : <td className="muted text-xs">{[l.specs, l.code && `code ${l.code}`, l.note].filter(Boolean).join("; ")}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {d.suppliers?.length ? (
        <Section title="Suppliers asked" count={String(d.suppliers.length)}>
          <ul className="divide-y divide-[var(--line)] px-3 pb-2">
            {d.suppliers.map((sp, i) => {
              const st = sp.status ? SUPPLIER_STATUS[sp.status] : undefined;
              return (
                <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span><span className="text-sm font-medium">{sp.name}</span>{sp.email && <span className="muted block text-xs">{sp.email}</span>}</span>
                  {st && <span className={clsx("rounded-md px-2 py-0.5 text-xs", st[1])}>{st[0]}</span>}
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      {d.quotes?.length ? (
        <Section title="Supplier responses" count={`${d.quotes.length} received`}>
          <div className="overflow-x-auto">
            <table className="table min-w-[34rem]">
              <thead><tr><th className="w-8" /><th>Supplier</th><th className="!text-right">Total price</th><th>Lead time</th><th>Received</th><th /></tr></thead>
              <tbody>{d.quotes.map((q, i) => <QuoteRow key={i} q={q} lines={lines} />)}</tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {d.totals && (
        <div className="border-t border-[var(--line)] px-3 py-2.5">
          <dl className="ml-auto grid w-fit grid-cols-[auto_auto] gap-x-6 gap-y-0.5 text-sm">
            {d.totals.subtotal !== undefined && <><dt className="muted text-right">Subtotal</dt><dd className="text-right tabular-nums">{money(d.totals.subtotal)}</dd></>}
            {d.totals.vat !== undefined && <><dt className="muted text-right">VAT{d.totals.vatRate !== undefined ? ` ${d.totals.vatRate}%` : ""}</dt><dd className="text-right tabular-nums">{money(d.totals.vat)}</dd></>}
            {d.totals.total !== undefined && <><dt className="text-right font-semibold">Total</dt><dd className="text-right font-semibold tabular-nums">{money(d.totals.total)}</dd></>}
          </dl>
        </div>
      )}

      {facts.length > 0 && (
        <div className="grid gap-2 border-t border-[var(--line)] p-3 sm:grid-cols-2 lg:grid-cols-3">
          {facts.map((f, i) => <div key={i} className="rounded-lg bg-black/[0.03] px-3 py-2 dark:bg-white/5"><div className="muted text-xs">{f.label}</div><div className="text-sm">{f.value}</div></div>)}
        </div>
      )}

      {(d.warnings?.length || d.links?.length) ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--line)] px-3 py-2.5 text-sm">
          {d.warnings?.map((w, i) => <span key={i} className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={14} /> {w}</span>)}
          <span className="flex-1" />
          {d.links?.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-600 hover:underline">{l.label} <ExternalLink size={13} /></a>)}
        </div>
      ) : null}

      {nothing && <p className="muted px-3 pb-3 text-sm">No further details were sent with this request.</p>}
    </div>
  );
}

function ApprovalListRow({ a, open, onToggle }: { a: ApprovalRow; open: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const d = useQuery({ queryKey: ["approval", a.id], queryFn: () => api<ApprovalDetail>(`/api/approvals/${a.id}`), enabled: open });
  const act = useMutation({
    mutationFn: (decision: "approve" | "reject") => post(`/api/approvals/${a.id}/decide`, { decision, comment: comment || undefined, contextHash: d.data?.request.contextHash }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["approvals"] }); void qc.invalidateQueries({ queryKey: ["approval", a.id] }); void qc.invalidateQueries({ queryKey: ["inbox"] }); },
  });
  const kind = POLICY_LABEL[a.policyKey];
  return (
    <li>
      <div className="flex items-center gap-3 px-2 py-2.5">
        <button className="btn !min-h-0 shrink-0 !p-1.5" onClick={onToggle} aria-expanded={open} aria-label={open ? "Hide details" : "Show details"}>
          <ChevronDown size={16} className={clsx("transition-transform", open && "rotate-180")} />
        </button>
        <button className="min-w-0 flex-1 text-left" onClick={onToggle}>
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{a.title}</span>
            {kind && <span className="chip text-xs">{kind}</span>}
            {a.status === "pending" && <QuestionChip state={a.questionState} />}
          </span>
          <span className="muted block text-xs">{a.ref} · {a.requestedByName ?? "ONE"} · {fmtDate(a.createdAt)}{a.stepName && a.status === "pending" ? ` · at: ${a.stepName}` : ""}</span>
        </button>
        <span className="shrink-0 text-right">
          {a.amount && <span className="block font-semibold tabular-nums">{eur(a.amount)}</span>}
          <span className="muted block text-xs">{a.lineCount ? `${a.lineCount} line${a.lineCount > 1 ? "s" : ""}${a.quoteCount ? ` · ${a.quoteCount} quote${a.quoteCount > 1 ? "s" : ""}` : ""}` : <StatusPill state={a.status === "approved" ? "done" : a.status === "pending" ? "waiting" : "cancelled"} label={a.status} />}</span>
        </span>
      </div>
      {open && (
        <div className="mb-3 ml-2 mr-2 rounded-lg border border-[var(--line)] p-3 sm:ml-11">
          {d.isLoading ? <p className="muted text-sm">Loading…</p> : d.error ? <ErrorNote error={d.error} /> : d.data && (
            <>
              <ApprovalDetailsView context={d.data.request.context} />
              {d.data.request.summary && <p className="mt-3 whitespace-pre-wrap text-sm">{d.data.request.summary}</p>}
              <ApprovalQuestions id={a.id} thread={d.data.thread ?? []} state={d.data.questionState} canAsk={d.data.canAsk} />
              <ErrorNote error={act.error} />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link to="/approvals/$id" params={{ id: a.id }} className="text-sm text-brand-600 hover:underline">Full details and history</Link>
                <span className="flex-1" />
                {d.data.canDecide && (rejecting ? (
                  <>
                    <input className="input !w-64" placeholder="Why are you rejecting it?" value={comment} onChange={(e) => setComment(e.target.value)} autoFocus />
                    <button className="btn" onClick={() => { setRejecting(false); setComment(""); }}>Cancel</button>
                    <button className="btn btn-danger" disabled={!comment.trim() || act.isPending} onClick={() => act.mutate("reject")}><XCircle size={16} /> Reject</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-danger" onClick={() => setRejecting(true)}><XCircle size={16} /> Reject</button>
                    <button className="btn !border-emerald-600 !bg-emerald-600 text-white" disabled={act.isPending} onClick={() => act.mutate("approve")}><CheckCircle2 size={16} /> Approve</button>
                  </>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function ApprovalsPage() {
  const can = useCan();
  const [view, setView] = useState<"mine" | "requested" | "all">("mine");
  const [status, setStatus] = useState("pending");
  const q = useQuery({ queryKey: ["approvals", view, status], queryFn: () => api<{ items: ApprovalRow[] }>(`/api/approvals?view=${view}&status=${status}`) });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const allOpen = !!q.data?.items.length && q.data.items.every((a) => open.has(a.id));
  return (
    <div>
      <PageHeader title="Approvals" subtitle="Purchases, leave, access and project changes waiting for a decision." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="tabs" role="tablist">
          {([["mine", "Waiting for me"], ["requested", "I asked for"], ...(can("approvals.request.view") ? [["all", "All in scope"]] : [])] as Array<[typeof view, string]>).map(([v, l]) => (
            <button key={v} role="tab" aria-selected={view === v} className="tab" onClick={() => setView(v)}>{l}</button>
          ))}
        </div>
        <select className="input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="pending,approved,rejected,withdrawn">All</option>
        </select>
      </div>
      <Card>
        <ErrorNote error={q.error} />
        {q.data?.items.length ? (
          <>
            <div className="-mt-1 mb-2 flex justify-end">
              <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setOpen(allOpen ? new Set() : new Set(q.data!.items.map((a) => a.id)))}>{allOpen ? "Collapse all" : "Expand all"}</button>
            </div>
            <ul className="-mx-2 divide-y divide-[var(--line)]">
              {q.data.items.map((a) => (
                <ApprovalListRow key={a.id} a={a} open={open.has(a.id)} onToggle={() => setOpen((o) => { const n = new Set(o); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })} />
              ))}
            </ul>
          </>
        ) : <Empty>{q.isLoading ? "Loading…" : "Nothing here."}</Empty>}
      </Card>
    </div>
  );
}

interface ApprovalDetail {
  request: { id: string; ref: string; title: string; summary: string; amount: string | null; status: string; policy: string; context: Record<string, unknown>; contextHash: string; createdAt: string; workflowInstanceId: string | null };
  requestedByName: string | null;
  steps: Array<{ idx: number; name: string; mode: string; status: string; approvers: string[]; dueAt: string | null; decidedAt: string | null }>;
  thread: ThreadEntry[]; questionState: QuestionState; canAsk: boolean;
  decisions: Array<{ stepIdx: number; decision: string; comment: string | null; decidedAt: string; approverName: string; delegationId: string | null }>;
  canDecide: boolean;
  canWithdraw: boolean;
}

const StepIcon = ({ s }: { s: string }) =>
  s === "approved" ? <CheckCircle2 size={16} className="text-emerald-500" /> : s === "rejected" ? <XCircle size={16} className="text-red-500" /> : s === "skipped" ? <CircleSlash size={16} className="muted" /> : s === "pending" ? <Clock size={16} className="text-amber-500" /> : <CircleDashed size={16} className="muted" />;

export function ApprovalPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const q = useQuery({ queryKey: ["approval", id], queryFn: () => api<ApprovalDetail>(`/api/approvals/${id}`) });
  const act = useMutation({
    mutationFn: (decision: string) => (decision === "withdraw" ? post(`/api/approvals/${id}/withdraw`, {}) : post(`/api/approvals/${id}/decide`, { decision, comment: comment || undefined, contextHash: q.data?.request.contextHash })),
    onSuccess: () => { void qc.invalidateQueries(); setComment(""); },
  });
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const d = q.data;
  return (
    <div className="space-y-4">
      <PageHeader title={d.request.title} subtitle={<>{d.request.ref} · requested by {d.requestedByName ?? "ONE"} · {fmtDate(d.request.createdAt)} · policy {d.request.policy}</>} />
      <ErrorNote error={act.error} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="What's being approved">
            {d.request.amount && <p className="mb-2 text-2xl font-semibold">{eur(d.request.amount)}</p>}
            {d.request.summary && <p className="mb-2 whitespace-pre-wrap text-sm">{d.request.summary}</p>}
            <ApprovalDetailsView context={d.request.context} />
            <ApprovalQuestions id={d.request.id} thread={d.thread ?? []} state={d.questionState} canAsk={d.canAsk} />
            {d.request.workflowInstanceId && <p className="mt-3 text-xs"><Link to="/workflows/$id" params={{ id: d.request.workflowInstanceId }} className="text-brand-600 hover:underline">Part of a workflow →</Link></p>}
          </Card>
          {(d.canDecide || d.canWithdraw) && (
            <Card title="Your decision">
              <textarea className="input mb-3 min-h-[4rem]" placeholder="Comment (required when rejecting)" value={comment} onChange={(e) => setComment(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                {d.canDecide && <>
                  <button className="btn !border-emerald-600 !bg-emerald-600 text-white" disabled={act.isPending} onClick={() => act.mutate("approve")}><CheckCircle2 size={16} /> Approve</button>
                  <button className="btn" disabled={act.isPending || !comment} onClick={() => act.mutate("request_changes")}>Request changes</button>
                  <button className="btn btn-danger" disabled={act.isPending || !comment} onClick={() => act.mutate("reject")}><XCircle size={16} /> Reject</button>
                </>}
                {d.canWithdraw && <button className="btn" onClick={() => act.mutate("withdraw")}>Withdraw</button>}
              </div>
            </Card>
          )}
        </div>
        <Card title="Approval chain">
          <ol className="space-y-3">
            {d.steps.map((s) => (
              <li key={s.idx} className="text-sm">
                <div className="flex items-center gap-2 font-medium"><StepIcon s={s.status} /> {s.name}</div>
                <div className="muted ml-6 text-xs">{s.status === "skipped" ? "Not needed (policy condition)" : s.approvers.join(", ") || "—"}{s.mode === "all" ? " · all must approve" : ""}</div>
                {d.decisions.filter((x) => x.stepIdx === s.idx).map((x, i) => (
                  <div key={i} className="ml-6 mt-1 text-xs">{x.approverName} <strong>{x.decision.replace("_", " ")}</strong>{x.delegationId ? " (delegated)" : ""} · {fmtDate(x.decidedAt)}{x.comment ? ` — “${x.comment}”` : ""}</div>
                ))}
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}

// ───────────────────────── Workflows ─────────────────────────
interface Def { key: string; name: string; description: string; category: string; publishedVersion: number | null; running: number; startForm: FieldDef[] | null }
interface InstanceRow { id: string; ref: string; title: string; status: string; definitionKey: string; startedAt: string; doneSteps: number; totalSteps: number; currentStep?: string | null; outcome?: string | null }

function StartForm({ def, onClose }: { def: Def; onClose: () => void }) {
  const org = useOrg();
  const navigate = useNavigate();
  const profiles = useQuery({ queryKey: ["profiles"], queryFn: () => api<{ items: Array<{ key: string; name: string; description: string }> }>("/api/people/profiles") });
  const [data, setData] = useState<Record<string, unknown>>({});
  const [people, setPeople] = useState<Record<string, Person[]>>({});
  const m = useMutation({ mutationFn: () => post<{ id: string }>("/api/workflows/instances", { definitionKey: def.key, context: data }), onSuccess: (r) => { onClose(); void navigate({ to: "/workflows/$id", params: { id: r.id } }); } });
  const set = (k: string, v: unknown) => setData((d) => ({ ...d, [k]: v }));
  return (
    <Modal title={`Start: ${def.name}`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={m.isPending} onClick={() => m.mutate()}><Play size={16} /> Start</button></>}>
      <ErrorNote error={m.error} />
      {(def.startForm ?? []).map((f) => {
        const label = `${f.label}${f.required ? " *" : ""}`;
        const v = String(data[f.key] ?? "");
        if (f.type === "person") return <Field key={f.key} label={label} hint={f.help}><PersonPicker multiple={false} value={people[f.key] ?? []} onChange={(p) => { setPeople((x) => ({ ...x, [f.key]: p })); set(f.key, p[0]?.id); }} /></Field>;
        if (f.type === "company") return <Field key={f.key} label={label}><select className="input" value={v} onChange={(e) => set(f.key, e.target.value)}><option value="">Choose…</option>{org.data?.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>;
        if (f.type === "department") return <Field key={f.key} label={label}><select className="input" value={v} onChange={(e) => set(f.key, e.target.value || undefined)}><option value="">—</option>{org.data?.departments.filter((x) => !data.companyId || x.companyId === data.companyId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>;
        if (f.type === "site") return <Field key={f.key} label={label}><select className="input" value={v} onChange={(e) => set(f.key, e.target.value || undefined)}><option value="">—</option>{org.data?.sites.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>;
        if (f.type === "checkbox") return <label key={f.key} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data[f.key]} onChange={(e) => set(f.key, e.target.checked)} /> {f.label}</label>;
        if (f.type === "select") {
          const opts = f.key === "profileKey" ? (profiles.data?.items.map((p) => [p.key, `${p.name} — ${p.description}`]) ?? []) : (f.options ?? []).map((o) => [o, o]);
          return <Field key={f.key} label={label}><select className="input" value={v} onChange={(e) => set(f.key, e.target.value)}><option value="">Choose…</option>{opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>;
        }
        return <Field key={f.key} label={label} hint={f.help}><input className="input" type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} value={v} onChange={(e) => set(f.key, e.target.value)} /></Field>;
      })}
    </Modal>
  );
}

export function WorkflowsPage() {
  const can = useCan();
  const [starting, setStarting] = useState<Def | null>(null);
  const [status, setStatus] = useState("running,needs_attention");
  const defs = useQuery({ queryKey: ["wf-defs"], queryFn: () => api<{ items: Def[] }>("/api/workflows/definitions"), enabled: can("workflow.definition.view") });
  const inst = useQuery({ queryKey: ["wf-inst", status], queryFn: () => api<{ items: InstanceRow[] }>(`/api/workflows/instances?status=${status}`) });
  return (
    <div className="space-y-4">
      <PageHeader title="Workflows" subtitle="Step-by-step processes like onboarding and leave, with every team's part tracked." />
      {defs.data && (
        <div className="grid gap-3 md:grid-cols-2">
          {defs.data.items.map((d) => (
            <Card key={d.key} title={<>{d.name} <span className="chip ml-1">v{d.publishedVersion ?? "draft"}</span></>} actions={d.startForm && d.startForm.length > 0 && (can("workflow.instance.start") || can("people.onboarding.manage")) && <button className="btn btn-primary !min-h-0 !py-1 text-xs" onClick={() => setStarting(d)}><Play size={14} /> Start</button>}>
              <p className="muted text-sm">{d.description}</p>
              <p className="mt-1 text-xs">{d.category} · {d.running} running{!d.startForm?.length ? " · started from requests" : ""}</p>
            </Card>
          ))}
        </div>
      )}
      <Card title="Instances" actions={<select className="input !w-auto !py-1 text-xs" value={status} onChange={(e) => setStatus(e.target.value)}><option value="running,needs_attention">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select>}>
        {inst.data?.items.length ? (
          <ul className="-mx-2 divide-y divide-[var(--line)]">
            {inst.data.items.map((i) => (
              <li key={i.id}>
                <Link to="/workflows/$id" params={{ id: i.id }} className="flex flex-wrap items-center justify-between gap-2 px-2 py-2.5 hover:bg-black/5 dark:hover:bg-white/5">
                  <span><span className="font-medium">{i.title}</span><span className="muted block text-xs">{i.ref} · {fmtDate(i.startedAt)}{i.currentStep ? ` · now: ${i.currentStep}` : ""}{i.outcome && i.outcome !== "completed" ? ` · ${i.outcome.replace(/_/g, " ")}` : ""}</span></span>
                  <span className="flex items-center gap-2 text-xs">
                    <span className="h-1.5 w-24 overflow-hidden rounded bg-black/10 dark:bg-white/10"><span className="block h-full bg-brand-500" style={{ width: `${(i.doneSteps / Math.max(1, i.totalSteps)) * 100}%` }} /></span>
                    {i.doneSteps}/{i.totalSteps}
                    <StatusPill state={i.status === "completed" ? "done" : i.status === "needs_attention" ? "waiting" : i.status === "cancelled" ? "cancelled" : "in_progress"} label={i.status.replace("_", " ")} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : <Empty>No workflows here.</Empty>}
      </Card>
      {starting && <StartForm def={starting} onClose={() => setStarting(null)} />}
    </div>
  );
}

interface InstanceDetail {
  instance: { id: string; ref: string; title: string; status: string; outcome?: string | null; businessKey?: string | null; definitionKey: string; version: number; startedAt: string; completedAt: string | null };
  stages: Array<{ key: string; name: string }>;
  summary?: Array<{ label: string; value: string | null }>;
  steps: Array<{
    key: string; stageKey: string; name: string; status: string; mandatory: boolean; workItemId?: string; workItemRef?: string; approvalId?: string; assigneeNames?: string[]; error?: string; redacted?: boolean;
    attempts?: number; output?: { outcome?: string; decision?: Record<string, unknown> } | null; dueAt?: string | null; approvalStatus?: string | null; approverNames?: string[] | null;
    form?: DecisionField[] | null; link?: string | null; waitingFor?: string[] | null; requirePermission?: string | null;
  }>;
  history?: Array<{ at: string; action: string; actor: string | null; after: Record<string, unknown> | null; reason: string | null }>;
  events?: Array<{ type: string; source: string | null; receivedAt: string; consumedStep: string | null }>;
  canManage: boolean;
}

/** "fusionhire.recruitment.offer_sent" → "offer sent". */
const evName = (t: string) => t.split(".").pop()!.replace(/_/g, " ");
const isUrl = (v: string | null | undefined) => !!v && /^https?:\/\//.test(v);
const HISTORY_LABEL: Record<string, string> = {
  "workflow.instance.started": "Started", "workflow.step.done": "Step done", "workflow.step.failed": "Step failed", "workflow.step.rewound": "Sent back",
  "workflow.step.decision": "Answered", "workflow.event.received": "Message from app", "workflow.instance.completed": "Completed", "workflow.instance.cancelled": "Closed",
  "workflow.instance.cancelled_by": "Cancelled", "workflow.step.retry": "Retried", "workflow.step.skip": "Skipped",
  "work.item.created": "Task created", "work.item.status_changed": "Task updated", "approvals.request.created": "Approval requested",
  "approvals.decision.approve": "Approved", "approvals.decision.reject": "Rejected", "approvals.decision.request_changes": "Changes requested",
  "approvals.request.approved": "Approval complete", "approvals.request.rejected": "Approval not given", "approvals.request.withdrawn": "Approval withdrawn",
};

export function WorkflowPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const q = useQuery({ queryKey: ["wf", id], queryFn: () => api<InstanceDetail>(`/api/workflows/instances/${id}`), refetchInterval: 15_000 });
  const act = useMutation({
    mutationFn: ({ step, action }: { step?: string; action: "retry" | "skip" | "cancel" }) => {
      const reason = prompt("Reason (recorded in the audit log)") ?? "";
      return action === "cancel" ? post(`/api/workflows/instances/${id}/cancel`, { reason }) : post(`/api/workflows/instances/${id}/steps/${step}/${action}`, { reason });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["wf", id] }),
  });
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const d = q.data;
  const tone: Record<string, string> = { done: "text-emerald-600", active: "text-brand-600", failed: "text-red-600", skipped: "muted", pending: "muted", cancelled: "muted" };
  const now = d.steps.filter((s) => (s.status === "active" || s.status === "failed") && !s.redacted);
  const overdue = (s: InstanceDetail["steps"][number]) => s.status === "active" && s.dueAt && new Date(s.dueAt) < new Date();
  const summary = (d.summary ?? []).filter((x) => x.value);
  return (
    <div className="space-y-4">
      <PageHeader title={d.instance.title} subtitle={<>{d.instance.ref} · {d.instance.definitionKey} v{d.instance.version} · started {fmtDate(d.instance.startedAt)}</>}
        actions={d.canManage && (d.instance.status === "running" || d.instance.status === "needs_attention") && <button className="btn btn-danger" onClick={() => act.mutate({ action: "cancel" })}>Cancel workflow</button>} />
      <ErrorNote error={act.error} />
      <div className="flex flex-wrap gap-2 text-sm">
        <StatusPill state={d.instance.status === "completed" ? "done" : d.instance.status === "needs_attention" ? "waiting" : d.instance.status === "cancelled" ? "cancelled" : "in_progress"} label={d.instance.status.replace("_", " ")} />
        {d.instance.outcome && d.instance.outcome !== d.instance.status && <span className="chip">{d.instance.outcome.replace(/_/g, " ")}</span>}
      </div>
      {summary.length > 0 && (
        <Card>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {summary.map((x) => (
              <div key={x.label} className="min-w-0">
                <dt className="muted text-xs">{x.label}</dt>
                <dd className="truncate font-medium">{isUrl(x.value) ? <a className="inline-flex items-center gap-1 text-brand-700 hover:underline" href={x.value!} target="_blank" rel="noopener noreferrer">Open <ExternalLink size={12} /></a> : x.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
      {now.length > 0 && (
        <Card title="Now" count={now.length}>
          <ul className="space-y-3 text-sm">
            {now.map((s) => (
              <li key={s.key} className={clsx("rounded-lg border p-3", s.status === "failed" ? "border-red-300" : overdue(s) ? "border-amber-300" : "border-[var(--line)]")}>
                <div className="flex flex-wrap items-start gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{s.name}</span>
                    {(s.attempts ?? 0) > 1 && <span className="chip ml-1 text-xs">round {s.attempts}</span>}
                    <span className="muted block text-xs">
                      {[
                        s.assigneeNames?.length ? s.assigneeNames.join(", ") : null,
                        s.approverNames?.length ? `Approver: ${s.approverNames.join(", ")}` : null,
                        s.waitingFor?.length ? `Waiting for: ${s.waitingFor.map(evName).join(" / ")}` : null,
                        s.dueAt ? `due ${fmtDate(s.dueAt)}` : null,
                      ].filter(Boolean).join(" · ")}
                      {overdue(s) && <span className="ml-1 font-semibold text-amber-700">overdue</span>}
                    </span>
                    {s.error && <span className="block text-xs text-red-600">{s.error}</span>}
                  </span>
                  <span className="flex flex-wrap gap-1">
                    {s.link && <a className="btn !min-h-0 !px-2 !py-0.5 text-xs" href={s.link} target="_blank" rel="noopener noreferrer">Open in app <ExternalLink size={11} /></a>}
                    {s.workItemId && <Link to="/work/$id" params={{ id: s.workItemId }} className="btn !min-h-0 !px-2 !py-0.5 text-xs">Task</Link>}
                    {s.approvalId && <Link to="/approvals/$id" params={{ id: s.approvalId }} className="btn !min-h-0 !px-2 !py-0.5 text-xs">Approval</Link>}
                    {d.canManage && s.status === "failed" && <button className="btn !min-h-0 !px-2 !py-0.5 text-xs" onClick={() => act.mutate({ step: s.key, action: "retry" })}>Retry</button>}
                    {d.canManage && <button className="btn !min-h-0 !px-2 !py-0.5 text-xs" onClick={() => act.mutate({ step: s.key, action: "skip" })}>Skip</button>}
                  </span>
                </div>
                {s.status === "active" && s.form && s.form.length > 0 && <StepForm instanceId={d.instance.id} stepKey={s.key} fields={s.form} />}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {d.stages.map((st) => {
          const steps = d.steps.filter((s) => s.stageKey === st.key);
          const allSkipped = steps.every((s) => s.status === "skipped");
          return (
            <Card key={st.key} title={<>{st.name} {allSkipped && <span className="chip ml-1">not needed</span>}</>} className={clsx(allSkipped && "opacity-60")}>
              <ul className="space-y-2 text-sm">
                {steps.map((s) => (
                  <li key={s.key} className="flex items-start gap-2">
                    <span className={clsx("mt-0.5 shrink-0 text-xs font-semibold uppercase", tone[s.status])}>{s.status === "done" ? "✓" : s.status === "active" ? "●" : s.status === "failed" ? "!" : s.status === "skipped" ? "–" : "○"}</span>
                    <span className="min-w-0 flex-1">
                      {s.workItemId && !s.redacted ? <Link to="/work/$id" params={{ id: s.workItemId }} className="hover:underline">{s.name}</Link> : s.approvalId && !s.redacted ? <Link to="/approvals/$id" params={{ id: s.approvalId }} className="hover:underline">{s.name}</Link> : <span className={clsx(s.status === "skipped" && "muted line-through")}>{s.name}</span>}
                      {!s.mandatory && <span className="chip ml-1">optional</span>}
                      {s.output?.outcome && s.status === "done" && !["done", "approved"].includes(s.output.outcome) && <span className="muted ml-1 text-xs">({s.output.outcome.replace(/_/g, " ")})</span>}
                      {s.assigneeNames && s.assigneeNames.length > 0 && <span className="muted block text-xs">{s.assigneeNames.join(", ")}</span>}
                      {s.error && <span className="block text-xs text-red-600">{s.error}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>
      {(d.history?.length ?? 0) > 0 && (
        <Card title="History" actions={<button className="btn btn-ghost btn-sm" onClick={() => setShowHistory((x) => !x)}>{showHistory ? "Hide" : `Show ${d.history!.length}`}</button>}>
          {showHistory && (
            <ul className="space-y-1.5 text-xs">
              {d.history!.map((h, i) => {
                const a = h.after ?? {};
                const what = [a.step ? String(a.step).replace(/_/g, " ") : null, a.outcome ? `→ ${String(a.outcome).replace(/_/g, " ")}` : null, a.type ? evName(String(a.type)) : null, a.to ? `to ${String(a.to).replace(/_/g, " ")}` : null].filter(Boolean).join(" ");
                return (
                  <li key={i} className="flex gap-2">
                    <span className="muted w-32 shrink-0">{fmtDate(h.at)}</span>
                    <span className="min-w-0 flex-1"><span className="font-medium">{HISTORY_LABEL[h.action] ?? h.action}</span>{what && <> · {what}</>}{h.reason && <span className="muted"> — {h.reason}</span>}{h.actor && <span className="muted"> · {h.actor}</span>}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

// ───────────────────────── Inspections ─────────────────────────
interface ItemDefI { key: string; text: string; type: string; required: boolean; min?: number; max?: number; unit?: string; severity: string; photoOnFail: boolean; photoRequired: boolean; help?: string }
interface RunDetail {
  run: { id: string; ref: string; title: string; status: string; score: number | null; failedCount: number; dueAt: string | null; workItemId: string | null; submittedAt: string | null };
  template: { name: string; sections: Array<{ key: string; title: string; items: ItemDefI[] }> };
  answers: Array<{ itemKey: string; value: unknown; result: string | null; note: string | null; attachments: string[]; correctiveItemId: string | null }>;
}

export function InspectionsPage() {
  const can = useCan();
  const navigate = useNavigate();
  const templates = useQuery({ queryKey: ["insp-templates"], queryFn: () => api<{ items: Array<{ key: string; name: string; description: string; correctiveSpaceKey: string | null }> }>("/api/inspections/templates") });
  const runs = useQuery({ queryKey: ["insp-runs"], queryFn: () => api<{ items: Array<{ id: string; ref: string; title: string; status: string; score: number | null; failedCount: number; criticalFailed: number; dueAt: string | null; createdAt: string }> }>("/api/inspections/runs"), enabled: can("inspections.run.view") });
  const start = useMutation({ mutationFn: (t: { key: string; correctiveSpaceKey: string | null }) => post<{ id: string }>("/api/inspections/runs", { templateKey: t.key, spaceKey: t.correctiveSpaceKey ?? "general" }), onSuccess: (r) => void navigate({ to: "/inspections/$id", params: { id: r.id } }) });
  return (
    <div className="space-y-4">
      <PageHeader title="Inspections" subtitle="Opening, closing, safety and quality checks. Failed items can raise fixes automatically." />
      <ErrorNote error={start.error} />
      <div className="grid gap-3 md:grid-cols-2">
        {templates.data?.items.map((t) => (
          <Card key={t.key} title={t.name} actions={can("inspections.run.perform") && <button className="btn btn-primary !min-h-0 !py-1 text-xs" onClick={() => start.mutate(t)}><Play size={14} /> Start now</button>}>
            <p className="muted text-sm">{t.description || "—"}</p>
          </Card>
        ))}
      </div>
      {runs.data && (
        <Card title="Recent">
          <ul className="-mx-2 divide-y divide-[var(--line)]">
            {runs.data.items.map((r) => (
              <li key={r.id}>
                <Link to="/inspections/$id" params={{ id: r.id }} className="flex flex-wrap items-center justify-between gap-2 px-2 py-2 hover:bg-black/5 dark:hover:bg-white/5">
                  <span><span className="font-medium">{r.title}</span><span className="muted block text-xs">{r.ref} · {fmtDate(r.createdAt)}</span></span>
                  <span className="flex items-center gap-2 text-xs">
                    {r.score !== null && <span className={clsx("font-semibold", r.criticalFailed ? "text-red-600" : r.failedCount ? "text-amber-600" : "text-emerald-600")}>{Number(r.score)}%</span>}
                    {r.criticalFailed > 0 && <span className="chip text-red-600">{r.criticalFailed} critical</span>}
                    <StatusPill state={r.status === "submitted" ? "done" : r.status === "in_progress" ? "in_progress" : "open"} label={r.status.replace("_", " ")} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export function InspectionRunPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["insp", id], queryFn: () => api<RunDetail>(`/api/inspections/runs/${id}`) });
  const answer = useMutation({ mutationFn: (b: { itemKey: string; value: unknown; note?: string; attachments?: string[] }) => post(`/api/inspections/runs/${id}/answers`, b), onSuccess: () => void qc.invalidateQueries({ queryKey: ["insp", id] }) });
  const submit = useMutation({ mutationFn: () => post<{ score: number; failed: number; critical: number }>(`/api/inspections/runs/${id}/submit`, {}), onSuccess: () => void qc.invalidateQueries() });
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const d = q.data;
  const ans = new Map(d.answers.map((a) => [a.itemKey, a]));
  const closed = d.run.status === "submitted";
  const total = d.template.sections.flatMap((s) => s.items).length;
  return (
    <div className="space-y-4">
      <PageHeader title={d.run.title} subtitle={<>{d.run.ref}{d.run.dueAt ? ` · due ${fmtDate(d.run.dueAt)}` : ""} · {d.answers.filter((a) => a.result).length}/{total} answered</>} />
      {closed && <div className="panel p-3 text-sm">Submitted {fmtDate(d.run.submittedAt)} · score <strong>{Number(d.run.score)}%</strong> · {d.run.failedCount} failed</div>}
      <ErrorNote error={answer.error ?? submit.error} />
      {d.template.sections.map((s) => (
        <Card key={s.key} title={s.title}>
          <ul className="space-y-4">
            {s.items.map((it) => {
              const a = ans.get(it.key);
              return (
                <li key={it.key} className={clsx("rounded-lg p-2", a?.result === "fail" && "bg-red-500/10")}>
                  <div className="text-sm font-medium">{it.text}{it.severity === "critical" && <span className="chip ml-2 text-red-600">critical</span>}{it.unit && <span className="muted ml-1 text-xs">({it.unit}{it.min !== undefined ? `, ${it.min}–${it.max}` : ""})</span>}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {it.type === "pass_fail" && ["pass", "fail", "na"].map((v) => (
                      <button key={v} disabled={closed} className={clsx("btn !min-w-[4.5rem] justify-center", a?.value === v && (v === "pass" ? "!border-emerald-600 !bg-emerald-600 text-white" : v === "fail" ? "!border-red-600 !bg-red-600 text-white" : "btn-primary"))} onClick={() => answer.mutate({ itemKey: it.key, value: v })}>{v === "na" ? "N/A" : v === "pass" ? "OK" : "Not OK"}</button>
                    ))}
                    {it.type === "number" && <input disabled={closed} className="input !w-32" type="number" step="any" inputMode="decimal" defaultValue={a?.value as number | undefined} onBlur={(e) => e.target.value && answer.mutate({ itemKey: it.key, value: Number(e.target.value) })} aria-label={it.text} />}
                    {it.type === "check" && <input disabled={closed} type="checkbox" className="h-6 w-6" checked={a?.value === true} onChange={(e) => answer.mutate({ itemKey: it.key, value: e.target.checked })} />}
                    {it.type === "text" && <input disabled={closed} className="input" defaultValue={a?.value as string | undefined} onBlur={(e) => answer.mutate({ itemKey: it.key, value: e.target.value })} />}
                    {!closed && (a?.result === "fail" || it.photoRequired || it.type === "photo") && <UploadButton label={a?.attachments.length ? `Photo ✓ (${a.attachments.length})` : "Add photo"} onUploaded={(ids) => answer.mutate({ itemKey: it.key, value: a?.value ?? "fail", attachments: ids.map((x) => x.id) })} />}
                    {a?.result === "fail" && <span className="text-xs font-medium text-red-600">Not OK</span>}
                    {a?.correctiveItemId && <Link to="/work/$id" params={{ id: a.correctiveItemId }} className="text-xs text-brand-600 hover:underline">Fix task →</Link>}
                  </div>
                  {!closed && a?.result === "fail" && <input className="input mt-2" placeholder="What's wrong?" defaultValue={a.note ?? ""} onBlur={(e) => e.target.value && answer.mutate({ itemKey: it.key, value: a.value, note: e.target.value })} />}
                </li>
              );
            })}
          </ul>
        </Card>
      ))}
      {!closed && <button className="btn btn-primary w-full justify-center !py-3" disabled={submit.isPending} onClick={() => submit.mutate()}>Submit inspection</button>}
    </div>
  );
}

// ───────────────────────── Assets ─────────────────────────
interface AssetRow { id: string; tag: string; class: string; name: string; make: string | null; model: string | null; serial: string | null; status: string; assignedPersonId: string | null; assignedName: string | null }
const CLASSES = ["laptop", "desktop", "phone", "sim", "tablet", "vehicle", "tool", "access_card", "key", "ppe", "uniform", "equipment", "other"];

export function AssetsPage() {
  const can = useCan();
  const qc = useQueryClient();
  const org = useOrg();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [assigning, setAssigning] = useState<AssetRow | null>(null);
  const [form, setForm] = useState<Record<string, string>>({ class: "laptop" });
  const [person, setPerson] = useState<Person[]>([]);
  const list = useQuery({ queryKey: ["assets", q], queryFn: () => api<{ items: AssetRow[] }>(`/api/assets?q=${encodeURIComponent(q)}`) });
  const create = useMutation({ mutationFn: () => post("/api/assets", { ...form, make: form.make || null, model: form.model || null, serial: form.serial || null }), onSuccess: () => { setAdding(false); setForm({ class: "laptop" }); void qc.invalidateQueries({ queryKey: ["assets"] }); } });
  const assign = useMutation({ mutationFn: () => post(`/api/assets/${assigning!.id}/assign`, { personId: person[0]!.id }), onSuccess: () => { setAssigning(null); setPerson([]); void qc.invalidateQueries(); } });
  const ret = useMutation({ mutationFn: (a: AssetRow) => post(`/api/assets/${a.id}/return`, { conditionIn: prompt("Condition on return?") ?? undefined }), onSuccess: () => void qc.invalidateQueries() });
  return (
    <div>
      <PageHeader title="Assets" subtitle="Laptops, phones, vehicles, tools, cards and keys — who has what, and its history." actions={can("assets.asset.manage") && <button className="btn btn-primary" onClick={() => setAdding(true)}><Plus size={16} /> Add asset</button>} />
      <input className="input mb-3 max-w-sm" placeholder="Search tag, name or serial" value={q} onChange={(e) => setQ(e.target.value)} />
      <ErrorNote error={assign.error ?? ret.error} />
      <Card>
        {list.data?.items.length ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>Tag</th><th>Asset</th><th>Class</th><th>Status</th><th>With</th><th /></tr></thead>
              <tbody>
                {list.data.items.map((a) => (
                  <tr key={a.id}>
                    <td className="font-mono text-xs">{a.tag}</td>
                    <td>{a.name}<span className="muted block text-xs">{[a.make, a.model, a.serial].filter(Boolean).join(" · ")}</span></td>
                    <td>{a.class.replace("_", " ")}</td>
                    <td><span className="chip">{a.status.replace("_", " ")}</span></td>
                    <td>{a.assignedName ?? "—"}</td>
                    <td className="text-right">{can("assets.asset.assign") && (a.assignedPersonId ? <button className="btn !min-h-0 !py-1 text-xs" onClick={() => ret.mutate(a)}>Return</button> : <button className="btn !min-h-0 !py-1 text-xs" onClick={() => setAssigning(a)}>Assign</button>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>{list.isLoading ? "Loading…" : "No assets yet."}</Empty>}
      </Card>
      {adding && (
        <Modal title="Add asset" onClose={() => setAdding(false)} footer={<><button className="btn" onClick={() => setAdding(false)}>Cancel</button><button className="btn btn-primary" disabled={!form.tag || !form.name || !form.companyId} onClick={() => create.mutate()}>Save</button></>}>
          <ErrorNote error={create.error} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tag (on the QR label)"><input className="input" value={form.tag ?? ""} onChange={(e) => setForm({ ...form, tag: e.target.value })} /></Field>
            <Field label="Class"><select className="input" value={form.class} onChange={(e) => setForm({ ...form, class: e.target.value })}>{CLASSES.map((c) => <option key={c}>{c}</option>)}</select></Field>
          </div>
          <Field label="Name"><input className="input" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. ThinkPad T14 Gen 5" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Make"><input className="input" value={form.make ?? ""} onChange={(e) => setForm({ ...form, make: e.target.value })} /></Field>
            <Field label="Model"><input className="input" value={form.model ?? ""} onChange={(e) => setForm({ ...form, model: e.target.value })} /></Field>
            <Field label="Serial"><input className="input" value={form.serial ?? ""} onChange={(e) => setForm({ ...form, serial: e.target.value })} /></Field>
          </div>
          <Field label="Company"><select className="input" value={form.companyId ?? ""} onChange={(e) => setForm({ ...form, companyId: e.target.value })}><option value="">Choose…</option>{org.data?.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        </Modal>
      )}
      {assigning && (
        <Modal title={`Assign ${assigning.tag}`} onClose={() => setAssigning(null)} footer={<><button className="btn" onClick={() => setAssigning(null)}>Cancel</button><button className="btn btn-primary" disabled={!person.length || assign.isPending} onClick={() => assign.mutate()}>Assign</button></>}>
          <Field label="To"><PersonPicker multiple={false} value={person} onChange={setPerson} /></Field>
        </Modal>
      )}
    </div>
  );
}

// ───────────────────────── Approval settings (thresholds) ─────────────────────────
export function ApprovalSettingsPage() {
  const can = useCan();
  const qc = useQueryClient();
  const params = useQuery({ queryKey: ["apr-params"], queryFn: () => api<{ items: Array<{ key: string; companyId: string | null; companyName: string | null; value: unknown; description: string; updatedAt: string }> }>("/api/approvals-admin/parameters") });
  const policies = useQuery({ queryKey: ["apr-policies"], queryFn: () => api<{ items: Array<{ key: string; version: number; name: string; description: string; spec: { steps: Array<{ key: string; name: string; when?: string; approver: { type: string; role?: string; team?: string } }> } }> }>("/api/approvals-admin/policies") });
  const put = useMutation({ mutationFn: (b: { key: string; companyId: string | null; value: number; description: string }) => fetch("/api/approvals-admin/parameters", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(b) }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).message); }), onSuccess: () => void qc.invalidateQueries({ queryKey: ["apr-params"] }) });
  return (
    <div className="space-y-4">
      <PageHeader title="Approval policies" subtitle="Who approves what, and from which amount." />
      <Card title="Thresholds & parameters">
        <ErrorNote error={put.error} />
        <table className="table">
          <thead><tr><th>Parameter</th><th>Applies to</th><th>Value</th><th>Notes</th></tr></thead>
          <tbody>
            {params.data?.items.map((p) => (
              <tr key={`${p.key}-${p.companyId}`}>
                <td className="font-mono text-xs">{p.key}</td>
                <td>{p.companyName ?? "Whole group"}</td>
                <td>{can("approvals.policy.admin") ? <input className="input !w-32" type="number" defaultValue={Number(p.value)} onBlur={(e) => Number(e.target.value) !== Number(p.value) && put.mutate({ key: p.key, companyId: p.companyId, value: Number(e.target.value), description: p.description.replace(/^PLACEHOLDER — /, "") })} /> : String(p.value)}</td>
                <td className={clsx("text-xs", p.description.startsWith("PLACEHOLDER") ? "text-amber-600" : "muted")}>{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {policies.data?.items.map((p) => (
        <Card key={p.key} title={<>{p.name} <span className="chip ml-1">v{p.version}</span></>}>
          <p className="muted mb-2 text-sm">{p.description}</p>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {p.spec.steps.map((s) => <li key={s.key}><strong>{s.name}</strong> — {s.approver.type === "role" ? `role ${s.approver.role}` : s.approver.type === "team_lead" ? `lead of ${s.approver.team}` : s.approver.type.replace("_", " ")}{s.when ? <span className="muted"> · only when {s.when.replace(/has\([^)]*\) && /g, "")}</span> : null}</li>)}
          </ol>
        </Card>
      ))}
    </div>
  );
}
